import { Box } from '@mui/material';
import Decimal from 'decimal.js';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Control, useWatch } from 'react-hook-form';

import { getCSSPropsForButtonPadding } from '@/components/form/baseInputs/Button/styles';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareInputRepeater } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import { InputRepeaterSumRow } from '@/components/form/formAwareInputs/FormAwareInputRepeater/InputRepeaterSumRow';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { determineDefaultAssetClassId } from '@/modules/assets/utils';
import { StructuredAssetsSubform_ValuationFragment } from '@/modules/entities/StructuredAssetsSubform/graphql/StructuredAssetsSubform.generated';
import { useAssetCategoryOptions } from '@/modules/entities/StructuredAssetsSubform/hooks/useAssetCategoryOptions';
import { StructuredAssetRowFields } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.fields';
import { StructuredAsset } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import { defaultValues as structuredAssetsDefaultValues } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import {
  getAssetValueSum,
  getOwnerAwareDefaultTaxSitus,
  shouldRequireStructuredAssetFields,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import { getEntityPrincipalClientIds } from '@/modules/entities/utils/getEntityPrincipalClientIds';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { AssetHoldingsFormShape } from '@/modules/estate/components/AddHoldingsFormModal/AddHoldingsFormModal.types';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useDefaultAssetClassId } from '@/modules/tenant/TenantDetailsContext/hooks/useDefaultAssetClassId';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { zIndexes } from '@/styles/zIndexes';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

export interface EditHoldingsFormProps {
  holdingsGroup?: EstatePage_EntityFragment;
  onSubmit: (data: AssetHoldingsFormShape) => void | Promise<void>;
  disabled?: boolean;
  /**
   * Initial assets to pre-populate the form
   */
  initialAssets?: StructuredAsset[];
  /**
   * The valuation containing the assets
   */
  valuation?: StructuredAssetsSubform_ValuationFragment | null;
}

export interface EditHoldingsFormRef {
  submit: () => void;
  reset: () => void;
}

export const EditHoldingsForm = forwardRef<
  EditHoldingsFormRef,
  EditHoldingsFormProps
>(
  (
    { holdingsGroup, onSubmit, disabled = false, initialAssets, valuation },
    ref
  ) => {
    const { assetClassesById } = useTenantDetailsContext();
    const defaultAssetClassId = useDefaultAssetClassId();
    const { primaryClients } = useUnguardedHouseholdDetailsContext();
    const ownerPrincipalIds = useMemo(
      () => getEntityPrincipalClientIds(holdingsGroup),
      [holdingsGroup]
    );
    const defaultTaxSitus = useMemo(() => {
      return getOwnerAwareDefaultTaxSitus({
        primaryClients,
        ownerPrincipalIds,
      });
    }, [primaryClients, ownerPrincipalIds]);

    const entityType = holdingsGroup
      ? getEntityTypeFromEntityKind(holdingsGroup.kind)
      : null;
    const defaultCategoryId = useMemo(() => {
      if (!entityType) return defaultAssetClassId;
      return determineDefaultAssetClassId(
        entityType,
        defaultAssetClassId,
        assetClassesById
      );
    }, [defaultAssetClassId, entityType, assetClassesById]);

    // Create structured assets data with initial assets or default empty asset
    const defaultStructuredAssetsData = useMemo(() => {
      // If we have initial assets, use those
      if (initialAssets && initialAssets.length > 0) {
        return {
          ...structuredAssetsDefaultValues,
          accountId: holdingsGroup?.subtype.designerAccount?.id,
          valuationId: valuation?.id,
          description: valuation?.description ?? '',
          assets: initialAssets,
        };
      }

      // Otherwise use default empty asset
      return {
        ...structuredAssetsDefaultValues,
        assets: defaultCategoryId
          ? [
              {
                categoryId: defaultCategoryId,
                title: '',
                value: new Decimal(0),
                integrationType: null,
                taxSitus: defaultTaxSitus,
                _originalTaxSitus: '',
              },
            ]
          : [],
      };
    }, [
      defaultCategoryId,
      defaultTaxSitus,
      initialAssets,
      holdingsGroup?.subtype.designerAccount?.id,
      valuation?.id,
      valuation?.description,
    ]);

    const formMethods = useForm<AssetHoldingsFormShape>({
      defaultValues: {
        holdingsGroupId: holdingsGroup?.id || null,
        holdingType: 'asset',
        structuredAssetsSubform: defaultStructuredAssetsData,
        valuationAsOfDate: new Date(),
      },
      mode: 'onChange', // Enable immediate validation/updates
    });

    useImperativeHandle(
      ref,
      () => ({
        submit: () => formMethods.handleSubmit(onSubmit)(),
        reset: () =>
          formMethods.reset({
            holdingsGroupId: holdingsGroup?.id || null,
            holdingType: 'asset',
            structuredAssetsSubform: defaultStructuredAssetsData,
            valuationAsOfDate: new Date(),
          }),
      }),
      [formMethods, holdingsGroup?.id, defaultStructuredAssetsData, onSubmit]
    );

    // Track the previous holdings group ID to avoid unnecessary resets
    const previousHoldingsGroupId = useRef<string | undefined>(
      holdingsGroup?.id
    );

    // Reset form when holdings group changes with clean defaults
    useEffect(() => {
      const currentHoldingsGroupId = holdingsGroup?.id;

      // Only reset if the holdings group actually changed (not on initial mount)
      if (
        previousHoldingsGroupId.current !== currentHoldingsGroupId &&
        previousHoldingsGroupId.current !== undefined
      ) {
        formMethods.reset({
          holdingsGroupId: currentHoldingsGroupId || null,
          holdingType: 'asset',
          structuredAssetsSubform: defaultStructuredAssetsData,
          valuationAsOfDate: new Date(),
        });
      }

      // Update the ref after the check
      previousHoldingsGroupId.current = currentHoldingsGroupId;
      // eslint-disable-next-line react-hooks/exhaustive-deps -- formMethods is stable and including it causes infinite resets
    }, [holdingsGroup?.id, defaultStructuredAssetsData]);

    // Initialize form with existing data when initialAssets or valuation changes
    useEffect(() => {
      if (initialAssets && initialAssets.length > 0) {
        const currentAssets =
          formMethods.getValues().structuredAssetsSubform?.assets || [];
        // Only reset if form is empty or doesn't have the expected data
        if (currentAssets.length === 0) {
          formMethods.reset({
            holdingsGroupId: holdingsGroup?.id || null,
            holdingType: 'asset',
            structuredAssetsSubform: defaultStructuredAssetsData,
            valuationAsOfDate: valuation?.effectiveDate
              ? new Date(valuation.effectiveDate)
              : new Date(),
          });
        }
      }
    }, [
      initialAssets,
      valuation?.effectiveDate,
      defaultStructuredAssetsData,
      holdingsGroup?.id,
      formMethods,
    ]);

    return (
      <FormProvider {...formMethods}>
        <EditFormContent
          holdingsGroup={holdingsGroup}
          control={formMethods.control}
          disabled={disabled}
          defaultCategoryId={defaultCategoryId}
          defaultTaxSitus={defaultTaxSitus}
          initialAssets={initialAssets}
        />
      </FormProvider>
    );
  }
);

EditHoldingsForm.displayName = 'EditHoldingsForm';

interface EditFormContentProps {
  holdingsGroup?: EstatePage_EntityFragment;
  control: Control<AssetHoldingsFormShape>;
  disabled: boolean;
  defaultCategoryId: string | null;
  defaultTaxSitus: string;
  initialAssets?: StructuredAsset[];
}

function EditFormContent({
  control,
  disabled,
  defaultCategoryId,
  defaultTaxSitus,
  initialAssets: _initialAssets,
}: EditFormContentProps) {
  // Watch the assets array to calculate the sum dynamically
  const assets = useWatch({
    name: 'structuredAssetsSubform.assets',
    control,
  }) as StructuredAsset[] | undefined;

  const assetValuesSum = useMemo(() => getAssetValueSum(assets), [assets]);

  // A single empty row may be submitted as "no assets", so don't require its
  // fields in that case.
  const assetFieldsRequired = useMemo(
    () => shouldRequireStructuredAssetFields(assets),
    [assets]
  );

  const { options: assetCategoryOptions, loading: formOptionsLoading } =
    useAssetCategoryOptions();

  // Use a key to force re-mounting when defaultCategoryId changes
  // This ensures proper initialization when switching between entities
  const repeaterKey = useMemo(() => {
    return `edit-asset-repeater-${defaultCategoryId}`;
  }, [defaultCategoryId]);

  return (
    <FormFieldsDisabledProvider
      isDisabled={Boolean(formOptionsLoading || disabled)}
    >
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          {/* Guard the initial render until we have the default category option */}
          {defaultCategoryId && (
            <FormAwareInputRepeater<AssetHoldingsFormShape>
              key={repeaterKey}
              name={'structuredAssetsSubform.assets'}
              emptyValue={{
                categoryId: defaultCategoryId,
                title: '',
                value: new Decimal(0),
                integrationType: null,
                taxSitus: defaultTaxSitus,
                _originalTaxSitus: '',
              }}
              control={control}
              shouldInit={false}
              keepAtLeastOneRow
              addAnotherItemText={`Add additional asset or account`}
              addAnotherItemButtonProps={{
                size: 'sm',
                variant: 'transparent',
                sx: {
                  ...getCSSPropsForButtonPadding('sm', false),
                },
              }}
              rightFooterContent={
                // Constrain to 75% of footer slot to align under the Value column.
                <Box display="flex" justifyContent="flex-end">
                  <Box sx={{ width: '75%' }}>
                    <InputRepeaterSumRow
                      content={formatCurrencyNoDecimals(assetValuesSum)}
                    />
                  </Box>
                </Box>
              }
              render={(i: number) => (
                <StructuredAssetRowFields
                  index={i}
                  options={assetCategoryOptions}
                  required={assetFieldsRequired}
                  // Select menus default to theme.zIndex.modal (400), which is
                  // below the drawer (theme.zIndex.drawer, 500); POPOVER (600)
                  // floats them above it.
                  selectMenuProps={{
                    disablePortal: false,
                    style: { zIndex: zIndexes.POPOVER },
                  }}
                />
              )}
            />
          )}
        </FormLayoutItem>
      </FormLayoutRow>
    </FormFieldsDisabledProvider>
  );
}
